import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from 'react';
import {useQuery} from '@tanstack/react-query';
import QUERY_KEYS from '../../constants/queryKeys';
import {handleGetGroupedChecklistsService} from '../../services/operations/checklist';
import {useDebounce} from '../../hooks/useDebunce';
import {useClientsContext, getLocationFilterParams} from '../clients/ClientsContextProvider';
import {
  ChecklistGroupClient,
  GroupedChecklistsResponse,
  IChecklist,
} from '../../pages/checklist/checklist';

interface ChecklistContextType {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedChecklists: Array<string | number>;
  setSelectedChecklists: React.Dispatch<
    React.SetStateAction<Array<string | number>>
  >;
  selectedChecklist: any;
  setSelectedChecklist: React.Dispatch<React.SetStateAction<any>>;
  showAddEditDialog: boolean;
  setShowAddEditDialog: React.Dispatch<React.SetStateAction<boolean>>;
  copyMode: boolean;
  setCopyMode: React.Dispatch<React.SetStateAction<boolean>>;

  groupedChecklists: GroupedChecklistsResponse | undefined;
  flatChecklists: IChecklist[];
  refetchGroupedChecklists: () => void;
  menuSelectedItem: string;
  setMenuSelectedItem: React.Dispatch<React.SetStateAction<string>>;
}

const ChecklistContext = createContext<ChecklistContextType | null>(null);

interface ChecklistProviderProps {
  children: ReactNode;
}

function flattenChecklistGroups(
  groups: ChecklistGroupClient[] | undefined,
): IChecklist[] {
  if (!groups) return [];
  const result: IChecklist[] = [];
  groups.forEach(client => {
    result.push(...client.own);
    client.branches.forEach(branch => {
      result.push(...branch.own);
      branch.subBranches.forEach(sub => {
        result.push(...sub.own);
      });
    });
  });
  return result;
}

export const ChecklistProvider = ({children}: ChecklistProviderProps) => {
  const {selectedLocation} = useClientsContext();

  const [searchText, setSearchText] = useState('');
  const [selectedChecklists, setSelectedChecklists] = useState<
    Array<string | number>
  >([]);
  const [selectedChecklist, setSelectedChecklist] = useState<any>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [copyMode, setCopyMode] = useState(false);
  const [menuSelectedItem, setMenuSelectedItem] = useState('');

  const debouncedSearch = useDebounce(searchText, 500);

  const {data: groupedChecklists, refetch: refetchGroupedChecklists} =
    useQuery<GroupedChecklistsResponse, Error>({
      queryKey: [
        QUERY_KEYS.GET_GROUPED_CHECKLISTS,
        selectedLocation,
        debouncedSearch,
        menuSelectedItem,
      ],
      queryFn: () => {
        const locationParams = getLocationFilterParams(selectedLocation);
        return handleGetGroupedChecklistsService({
          ...locationParams,
          clientId: locationParams.clientId!,
          search: debouncedSearch,
          ...(menuSelectedItem !== 'archived' && {locationStatus: 'active'}),
        });
      },
      enabled: !!selectedLocation,
    });

  const flatChecklists = useMemo(
    () => flattenChecklistGroups(groupedChecklists?.groups),
    [groupedChecklists],
  );

  useEffect(() => {
    setSelectedChecklists([]);
  }, [selectedLocation]);

  return (
    <ChecklistContext.Provider
      value={{
        searchText,
        setSearchText,
        selectedChecklists,
        setSelectedChecklists,
        selectedChecklist,
        setSelectedChecklist,
        showAddEditDialog,
        setShowAddEditDialog,
        copyMode,
        setCopyMode,

        groupedChecklists,
        flatChecklists,
        refetchGroupedChecklists,

        menuSelectedItem,
        setMenuSelectedItem,
      }}>
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklistContext = () => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error(
      'useChecklistContext must be used within a ChecklistProvider',
    );
  }
  return context;
};
